/**
 * Sprint P21 · Feb 2026 · Cockpit Geological Mesh Layer.
 *
 * P0 upgrade (Feb 2026): mesh vertices come from the backend in
 * world-UTM (E, N, Elev) triples; they are converted to local scene space
 * via the shared `worldToScene` helper before being pushed to the GPU. This
 * keeps every layer aligned with the block model and drillholes and avoids
 * the "tiny-dot" bug.
 */
import { useMemo } from "react";
import * as THREE from "three";
import { isValidCoordinate } from "@/lib/sceneCoords";


export default function GeoMeshLayer({
  model,
  visibility = true,
  wireframe = false,
  opacity = 0.65,
  origin,
  verticalExaggeration = 1,
}) {
  const geometry = useMemo(() => {
    if (!model || !visibility || !origin) return null;
    const vertices = model.vertices || [];
    const faces    = model.faces    || [];
    if (!vertices.length || !faces.length) return null;

    const oe = origin.easting   ?? 0;
    const on = origin.northing  ?? 0;
    const oz = origin.elevation ?? 0;

    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(vertices.length * 3);
    let rejected = 0;
    for (let i = 0; i < vertices.length; i++) {
      const v = vertices[i];
      const e = Number(v?.[0]), n = Number(v?.[1]), z = Number(v?.[2]);
      if (!isValidCoordinate(e, n, z)) {
        rejected++;
        positions[i * 3]     = 0;
        positions[i * 3 + 1] = 0;
        positions[i * 3 + 2] = 0;
        continue;
      }
      positions[i * 3]     = e - oe;
      positions[i * 3 + 1] = (z - oz) * verticalExaggeration;
      positions[i * 3 + 2] = -(n - on);
    }
    const indices = new Uint32Array(faces.length * 3);
    for (let i = 0; i < faces.length; i++) {
      indices[i * 3]     = faces[i][0];
      indices[i * 3 + 1] = faces[i][1];
      indices[i * 3 + 2] = faces[i][2];
    }
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setIndex(new THREE.BufferAttribute(indices, 1));
    geo.computeVertexNormals();
    geo.computeBoundingBox();
    geo.computeBoundingSphere();
    if (rejected) console.warn(`GeoMeshLayer ${model.id}: rejected ${rejected} invalid vertices`);
    return geo;
  }, [model, visibility, origin, verticalExaggeration]);

  if (!visibility || !geometry) return null;
  const colour = model.colour || "#EAB308";
  return (
    <mesh geometry={geometry}
          castShadow receiveShadow
          userData={{ modelId: model.id, kind: model.kind,
                       coordinateSpace: "local_scene" }}>
      <meshStandardMaterial color={colour}
                            transparent={opacity < 1}
                            opacity={opacity}
                            wireframe={wireframe}
                            side={THREE.DoubleSide}
                            metalness={0.15}
                            roughness={0.55} />
    </mesh>
  );
}
